import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUtils';
import { 
  Search as SearchIcon, MapPin, Filter, X, Shield, Crown, Lock, Eye,
  RotateCcw, ChevronDown, ChevronUp, User, MessageCircle, Navigation
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { OnlineIndicator } from '../components/OnlineIndicator';
import { Slider } from '../components/ui/slider';
import SubscriptionRequired from '../components/SubscriptionRequired';

const FilterSectionComponent = React.memo(({ title, section, expanded, onToggle, children }) => (
  <div className="border-b border-white/10 pb-4 mb-4">
    <button
      onClick={() => onToggle(section)}
      className="flex items-center justify-between w-full text-left mb-3 hover:text-gold transition-colors"
    >
      <span className="text-sm font-semibold text-gold uppercase tracking-wider">{title}</span>
      {expanded ? (
        <ChevronUp className="w-4 h-4 text-gray-500" />
      ) : (
        <ChevronDown className="w-4 h-4 text-gray-500" />
      )}
    </button>
    {expanded && (
      <div className="space-y-4">
        {children}
      </div>
    )}
  </div>
));

const PROFILE_TYPES = [
  { value: 'all', label: 'Tous' },
  { value: 'homme', label: 'Homme' },
  { value: 'femme', label: 'Femme' },
  { value: 'couple', label: 'Couple' }
];

const SEXUAL_ORIENTATIONS = [
  { value: 'all', label: 'Toutes' },
  { value: 'hetero', label: 'Hétéro' },
  { value: 'hetero_curieux', label: 'Hétéro curieux' },
  { value: 'bi', label: 'Bi' },
  { value: 'lesbienne', label: 'Lesbienne' },
  { value: 'gay', label: 'Gay' }
];

const EYE_COLORS = [
  { value: 'all', label: 'Toutes' },
  { value: 'noirs', label: 'Noirs' },
  { value: 'marrons', label: 'Marrons' },
  { value: 'verts', label: 'Verts' },
  { value: 'bleus', label: 'Bleus' }
];

const BODY_TYPES = [
  { value: 'all', label: 'Tous' },
  { value: 'mince', label: 'Mince' },
  { value: 'sportif', label: 'Sportif' },
  { value: 'moyenne', label: 'Dans la moyenne' },
  { value: 'rondeurs', label: 'Quelques rondeurs' },
  { value: 'rond', label: 'Rond' }
];

const ETHNICITIES = [
  { value: 'all', label: 'Toutes' },
  { value: 'africain', label: 'Africain(e)' },
  { value: 'arabe', label: 'Arabe' },
  { value: 'asiatique', label: 'Asiatique' },
  { value: 'caribeen', label: 'Caribéen(ne)' },
  { value: 'caucasien', label: 'Caucasien(ne)' },
  { value: 'latine', label: 'Latine' },
  { value: 'autre', label: 'Autre' }
];

const SMOKER_STATUS = [
  { value: 'all', label: 'Tous' },
  { value: 'non_fumeur', label: 'Non fumeur' },
  { value: 'occasionnel', label: 'De temps en temps' },
  { value: 'fumeur', label: 'Fumeur' },
  { value: 'vapoteur', label: 'Vapoteur' }
];

const EXPERIENCE_LEVELS = [
  { value: 'all', label: 'Tous' },
  { value: 'debutant', label: 'Débutant' },
  { value: 'occasionnel', label: 'Occasionnel' },
  { value: 'experimente', label: 'Expérimenté' }
];

const DISTANCE_OPTIONS = [
  { value: 0, label: 'Toutes distances' },
  { value: 10, label: '10 km' },
  { value: 25, label: '25 km' },
  { value: 50, label: '50 km' },
  { value: 100, label: '100 km' },
  { value: 200, label: '200 km' }
];

const ENVIES = [
  { value: 'soft', label: 'Soft' },
  { value: 'hard', label: 'Hard' },
  { value: 'fetish', label: 'Fétish' },
  { value: 'exhib', label: 'Exhib' },
  { value: 'voyeur', label: 'Voyeur' },
  { value: 'melangisme', label: 'Mélangisme' },
  { value: 'echangisme', label: 'Échangisme' },
  { value: 'triolisme', label: 'Triolisme' },
  { value: 'gang_bang', label: 'Gang bang' },
  { value: 'bdsm', label: 'BDSM' },
  { value: 'sm', label: 'SM' },
  { value: 'domination', label: 'Domination' },
  { value: 'soumission', label: 'Soumission' },
  { value: 'candaulisme', label: 'Candaulisme' },
  { value: 'webcam', label: 'Webcam' },
  { value: 'photos', label: 'Photos' },
  { value: 'videos', label: 'Vidéos' },
  { value: 'massage', label: 'Massage' },
  { value: 'naturisme', label: 'Naturisme' },
  { value: 'libertinage', label: 'Libertinage' }
];

export default function SearchPage() {
  const { user, api } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showFilterHint, setShowFilterHint] = useState(false);
  const [totalProfiles, setTotalProfiles] = useState(0);
  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    physical: true,
    preferences: false,
    envies: false,
    coupleMan: false,
    coupleWoman: false
  });
  
  const [filters, setFilters] = useState({
    pseudo: '',
    location: '',
    profile_type: 'all',
    sexual_orientation: 'all',
    age_min: 18,
    age_max: 99,
    height_min: 140,
    height_max: 220,
    eye_color: 'all',
    body_type: 'all',
    ethnicity: 'all',
    smoker: 'all',
    experience_level: 'all',
    envies: [],
    // Geolocation filters
    max_distance: 0,
    // Couple-specific filters for man
    man_sexual_orientation: 'all',
    man_age_min: 18,
    man_age_max: 99,
    man_height_min: 140,
    man_height_max: 220,
    man_eye_color: 'all',
    man_body_type: 'all',
    man_ethnicity: 'all',
    man_smoker: 'all',
    man_experience_level: 'all',
    // Couple-specific filters for woman
    woman_sexual_orientation: 'all',
    woman_age_min: 18,
    woman_age_max: 99,
    woman_height_min: 140,
    woman_height_max: 220,
    woman_eye_color: 'all',
    woman_body_type: 'all',
    woman_ethnicity: 'all',
    woman_smoker: 'all',
    woman_experience_level: 'all'
  });

  const isCouple = filters.profile_type === 'couple';
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState(false);
  const canSearch = user?.permissions?.can_search === true;
  
  // Geolocation state
  const [userLocation, setUserLocation] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [geoError, setGeoError] = useState(null);

  // Get user's current position
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      setGeoError("La géolocalisation n'est pas supportée par votre navigateur");
      return;
    }
    
    setGeoLoading(true);
    setGeoError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setGeoLoading(false);
        toast.success('Position GPS obtenue');
      },
      (error) => {
        setGeoLoading(false);
        switch(error.code) {
          case error.PERMISSION_DENIED:
            setGeoError("Vous avez refusé l'accès à votre position");
            break;
          case error.POSITION_UNAVAILABLE:
            setGeoError("Position non disponible");
            break;
          case error.TIMEOUT:
            setGeoError("Délai d'attente dépassé");
            break;
          default:
            setGeoError("Erreur de géolocalisation");
        }
        toast.error("Impossible d'obtenir votre position");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  useEffect(() => {
    // Only search if user has permission
    if (canSearch) {
      searchProfiles();
    }
  }, []);

  const searchProfiles = async () => {
    // Check permission first
    if (!canSearch) {
      setShowSubscriptionPopup(true);
      return;
    }
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Only add non-default filters
      if (filters.pseudo) params.append('pseudo', filters.pseudo);
      if (filters.location) params.append('location', filters.location);
      if (filters.profile_type && filters.profile_type !== 'all') params.append('profile_type', filters.profile_type);
      
      // Geolocation filters
      if (userLocation && filters.max_distance > 0) {
        params.append('latitude', userLocation.latitude);
        params.append('longitude', userLocation.longitude);
        params.append('max_distance', filters.max_distance);
        params.append('sort', 'distance');
      } else if (userLocation) {
        // Include location for distance calculation even without max_distance filter
        params.append('latitude', userLocation.latitude);
        params.append('longitude', userLocation.longitude);
      }
      
      // Envies filter
      if (filters.envies && filters.envies.length > 0) {
        params.append('envies', filters.envies.join(','));
      }
      
      if (isCouple) {
        // Couple-specific filters for man
        if (filters.man_sexual_orientation && filters.man_sexual_orientation !== 'all') params.append('man_sexual_orientation', filters.man_sexual_orientation);
        if (filters.man_age_min > 18) params.append('man_age_min', filters.man_age_min);
        if (filters.man_age_max < 99) params.append('man_age_max', filters.man_age_max);
        if (filters.man_height_min > 140) params.append('man_height_min', filters.man_height_min);
        if (filters.man_height_max < 220) params.append('man_height_max', filters.man_height_max);
        if (filters.man_eye_color && filters.man_eye_color !== 'all') params.append('man_eye_color', filters.man_eye_color);
        if (filters.man_body_type && filters.man_body_type !== 'all') params.append('man_body_type', filters.man_body_type);
        if (filters.man_ethnicity && filters.man_ethnicity !== 'all') params.append('man_ethnicity', filters.man_ethnicity);
        if (filters.man_smoker && filters.man_smoker !== 'all') params.append('man_smoker', filters.man_smoker);
        if (filters.man_experience_level && filters.man_experience_level !== 'all') params.append('man_experience_level', filters.man_experience_level);
        
        // Couple-specific filters for woman
        if (filters.woman_sexual_orientation && filters.woman_sexual_orientation !== 'all') params.append('woman_sexual_orientation', filters.woman_sexual_orientation);
        if (filters.woman_age_min > 18) params.append('woman_age_min', filters.woman_age_min);
        if (filters.woman_age_max < 99) params.append('woman_age_max', filters.woman_age_max);
        if (filters.woman_height_min > 140) params.append('woman_height_min', filters.woman_height_min);
        if (filters.woman_height_max < 220) params.append('woman_height_max', filters.woman_height_max);
        if (filters.woman_eye_color && filters.woman_eye_color !== 'all') params.append('woman_eye_color', filters.woman_eye_color);
        if (filters.woman_body_type && filters.woman_body_type !== 'all') params.append('woman_body_type', filters.woman_body_type);
        if (filters.woman_ethnicity && filters.woman_ethnicity !== 'all') params.append('woman_ethnicity', filters.woman_ethnicity);
        if (filters.woman_smoker && filters.woman_smoker !== 'all') params.append('woman_smoker', filters.woman_smoker);
        if (filters.woman_experience_level && filters.woman_experience_level !== 'all') params.append('woman_experience_level', filters.woman_experience_level);
      } else {
        // Standard filters for non-couple
        if (filters.sexual_orientation && filters.sexual_orientation !== 'all') params.append('sexual_orientation', filters.sexual_orientation);
        if (filters.age_min > 18) params.append('age_min', filters.age_min);
        if (filters.age_max < 99) params.append('age_max', filters.age_max);
        if (filters.height_min > 140) params.append('height_min', filters.height_min);
        if (filters.height_max < 220) params.append('height_max', filters.height_max);
        if (filters.eye_color && filters.eye_color !== 'all') params.append('eye_color', filters.eye_color);
        if (filters.body_type && filters.body_type !== 'all') params.append('body_type', filters.body_type);
        if (filters.ethnicity && filters.ethnicity !== 'all') params.append('ethnicity', filters.ethnicity);
        if (filters.smoker && filters.smoker !== 'all') params.append('smoker', filters.smoker);
        if (filters.experience_level && filters.experience_level !== 'all') params.append('experience_level', filters.experience_level);
      }
      
      const response = await api.get(`/search?${params.toString()}`);
      // Handle both array and object response format
      const data = response.data;
      setProfiles(Array.isArray(data) ? data : (data.profiles || []));
      setTotalProfiles(data.total || (Array.isArray(data) ? data.length : data.profiles?.length || 0));
      setShowFilterHint(data.show_filter_hint || false);
    } catch (error) {
      if (error.response?.status === 403) {
        setShowSubscriptionPopup(true);
      } else {
        toast.error('Erreur lors de la recherche');
      }
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      pseudo: '',
      location: '',
      profile_type: 'all',
      sexual_orientation: 'all',
      age_min: 18,
      age_max: 99,
      height_min: 140,
      height_max: 220,
      eye_color: 'all',
      body_type: 'all',
      ethnicity: 'all',
      smoker: 'all',
      experience_level: 'all',
      envies: [],
      man_sexual_orientation: 'all',
      man_age_min: 18,
      man_age_max: 99,
      man_height_min: 140,
      man_height_max: 220,
      man_eye_color: 'all',
      man_body_type: 'all',
      man_ethnicity: 'all',
      man_smoker: 'all',
      man_experience_level: 'all',
      woman_sexual_orientation: 'all',
      woman_age_min: 18,
      woman_age_max: 99,
      woman_height_min: 140,
      woman_height_max: 220,
      woman_eye_color: 'all',
      woman_body_type: 'all',
      woman_ethnicity: 'all',
      woman_smoker: 'all',
      woman_experience_level: 'all'
    });
  };

  const hasActiveFilters = () => {
    return filters.pseudo || filters.location || 
           (filters.profile_type && filters.profile_type !== 'all') || 
           (filters.sexual_orientation && filters.sexual_orientation !== 'all') || 
           filters.age_min > 18 || filters.age_max < 99 ||
           filters.height_min > 140 || filters.height_max < 220 || 
           (filters.eye_color && filters.eye_color !== 'all') ||
           (filters.body_type && filters.body_type !== 'all') || 
           (filters.ethnicity && filters.ethnicity !== 'all') || 
           (filters.smoker && filters.smoker !== 'all') || 
           (filters.experience_level && filters.experience_level !== 'all') ||
           (filters.envies && filters.envies.length > 0) ||
           // Couple man filters
           (filters.man_sexual_orientation && filters.man_sexual_orientation !== 'all') ||
           filters.man_age_min > 18 || filters.man_age_max < 99 ||
           (filters.man_eye_color && filters.man_eye_color !== 'all') ||
           (filters.man_body_type && filters.man_body_type !== 'all') ||
           (filters.man_ethnicity && filters.man_ethnicity !== 'all') ||
           (filters.man_smoker && filters.man_smoker !== 'all') ||
           (filters.man_experience_level && filters.man_experience_level !== 'all') ||
           // Couple woman filters
           (filters.woman_sexual_orientation && filters.woman_sexual_orientation !== 'all') ||
           filters.woman_age_min > 18 || filters.woman_age_max < 99 ||
           (filters.woman_eye_color && filters.woman_eye_color !== 'all') ||
           (filters.woman_body_type && filters.woman_body_type !== 'all') ||
           (filters.woman_ethnicity && filters.woman_ethnicity !== 'all') ||
           (filters.woman_smoker && filters.woman_smoker !== 'all') ||
           (filters.woman_experience_level && filters.woman_experience_level !== 'all');
  };

  const toggleSectionCb = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }, []);

  const filtersContent = (
    <div className="space-y-2">
      {/* Search by name */}
      <FilterSectionComponent title="Identité" section="identity" expanded={expandedSections["identity"]} onToggle={toggleSectionCb}>
        <div className="space-y-2">
          <Label className="text-gray-400 text-xs">Nom du profil</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              type="text"
              placeholder="Rechercher un pseudo..."
              value={filters.pseudo}
              onChange={(e) => setFilters(prev => ({ ...prev, pseudo: e.target.value }))}
              className="input-gold pl-10 text-sm"
              data-testid="filter-pseudo"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-gray-400 text-xs">Lieu</Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              type="text"
              placeholder="Ville ou région..."
              value={filters.location}
              onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
              className="input-gold pl-10 text-sm"
              data-testid="filter-location"
            />
          </div>
        </div>

        {/* Geolocation Search */}
        <div className="space-y-2 p-3 bg-dark-lighter rounded-lg border border-gold/20">
          <Label className="text-gold text-xs font-semibold flex items-center gap-2">
            <Navigation className="w-4 h-4" />
            Recherche par distance
          </Label>
          
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={getUserLocation}
              disabled={geoLoading}
              className={`flex-1 text-xs ${userLocation ? 'bg-green-600 hover:bg-green-700' : 'bg-gold/20 hover:bg-gold/30'} text-white`}
              data-testid="btn-get-location"
            >
              {geoLoading ? (
                'Localisation...'
              ) : userLocation ? (
                <>✓ Position obtenue</>
              ) : (
                <>Ma position GPS</>
              )}
            </Button>
          </div>
          
          {geoError && (
            <p className="text-red-400 text-xs">{geoError}</p>
          )}
          
          {userLocation && (
            <div className="space-y-2 mt-2">
              <Label className="text-gray-400 text-xs">Distance maximum</Label>
              <Select
                value={filters.max_distance.toString()}
                onValueChange={(value) => setFilters(prev => ({ ...prev, max_distance: parseInt(value) }))}
              >
                <SelectTrigger className="bg-transparent border-white/20 text-white text-sm" data-testid="filter-distance">
                  <SelectValue placeholder="Toutes distances" />
                </SelectTrigger>
                <SelectContent className="bg-dark-paper border-white/10">
                  {DISTANCE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value.toString()}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-gray-400 text-xs">Sexe</Label>
          <Select
            value={filters.profile_type}
            onValueChange={(value) => setFilters(prev => ({ ...prev, profile_type: value }))}
          >
            <SelectTrigger className="bg-transparent border-white/20 text-white text-sm" data-testid="filter-profile-type">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent className="bg-dark-paper border-white/10">
              {PROFILE_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!isCouple && (
          <>
            <div className="space-y-2">
              <Label className="text-gray-400 text-xs">Orientation sexuelle</Label>
              <Select
                value={filters.sexual_orientation}
                onValueChange={(value) => setFilters(prev => ({ ...prev, sexual_orientation: value }))}
              >
                <SelectTrigger className="bg-transparent border-white/20 text-white text-sm" data-testid="filter-orientation">
                  <SelectValue placeholder="Toutes" />
                </SelectTrigger>
                <SelectContent className="bg-dark-paper border-white/10">
                  {SEXUAL_ORIENTATIONS.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label className="text-gray-400 text-xs">Âge</Label>
                <span className="text-xs text-gold">{filters.age_min} - {filters.age_max} ans</span>
              </div>
              <div className="px-2">
                <Slider
                  value={[filters.age_min, filters.age_max]}
                  onValueChange={(values) => {
                    if (values && values.length === 2) {
                      setFilters(prev => ({ ...prev, age_min: values[0], age_max: values[1] }));
                    }
                  }}
                  min={18}
                  max={99}
                  step={1}
                  minStepsBetweenThumbs={1}
                  className="w-full"
                  data-testid="age-slider"
                />
              </div>
            </div>
          </>
        )}

        {isCouple && (
          <p className="text-xs text-gray-500 italic">
            Pour les couples, utilisez les filtres "Critères Homme" et "Critères Femme" ci-dessous.
          </p>
        )}
      </FilterSectionComponent>

      {/* Physical criteria (only for non-couples) */}
      {!isCouple && (
        <FilterSectionComponent title="Physique" section="physical" expanded={expandedSections["physical"]} onToggle={toggleSectionCb}>
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <Label className="text-gray-400 text-xs">Taille</Label>
            <span className="text-xs text-gold">{filters.height_min} - {filters.height_max} cm</span>
          </div>
          <div className="px-2">
            <Slider
              value={[filters.height_min, filters.height_max]}
              onValueChange={(values) => {
                if (values && values.length === 2) {
                  setFilters(prev => ({ ...prev, height_min: values[0], height_max: values[1] }));
                }
              }}
              min={140}
              max={220}
              step={1}
              minStepsBetweenThumbs={1}
              className="w-full"
              data-testid="height-slider"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-gray-400 text-xs">Couleur des yeux</Label>
          <Select
            value={filters.eye_color}
            onValueChange={(value) => setFilters(prev => ({ ...prev, eye_color: value }))}
          >
            <SelectTrigger className="bg-transparent border-white/20 text-white text-sm" data-testid="filter-eye-color">
              <SelectValue placeholder="Toutes" />
            </SelectTrigger>
            <SelectContent className="bg-dark-paper border-white/10">
              {EYE_COLORS.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-gray-400 text-xs">Physique</Label>
          <Select
            value={filters.body_type}
            onValueChange={(value) => setFilters(prev => ({ ...prev, body_type: value }))}
          >
            <SelectTrigger className="bg-transparent border-white/20 text-white text-sm" data-testid="filter-body-type">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent className="bg-dark-paper border-white/10">
              {BODY_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="text-gray-400 text-xs">Origine</Label>
          <Select
            value={filters.ethnicity}
            onValueChange={(value) => setFilters(prev => ({ ...prev, ethnicity: value }))}
          >
            <SelectTrigger className="bg-transparent border-white/20 text-white text-sm" data-testid="filter-ethnicity">
              <SelectValue placeholder="Toutes" />
            </SelectTrigger>
            <SelectContent className="bg-dark-paper border-white/10">
              {ETHNICITIES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </FilterSectionComponent>
      )}

      {/* Preferences (only for non-couples) */}
      {!isCouple && (
        <FilterSectionComponent title="Préférences" section="preferences" expanded={expandedSections["preferences"]} onToggle={toggleSectionCb}>
          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Fumeur</Label>
            <Select
              value={filters.smoker}
              onValueChange={(value) => setFilters(prev => ({ ...prev, smoker: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm" data-testid="filter-smoker">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {SMOKER_STATUS.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Niveau d'expérience</Label>
            <Select
              value={filters.experience_level}
              onValueChange={(value) => setFilters(prev => ({ ...prev, experience_level: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm" data-testid="filter-experience">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {EXPERIENCE_LEVELS.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </FilterSectionComponent>
      )}

      {/* Couple Filters - Man */}
      {isCouple && (
        <FilterSectionComponent title="Critères Homme" section="coupleMan" expanded={expandedSections["coupleMan"]} onToggle={toggleSectionCb}>
          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Orientation sexuelle</Label>
            <Select
              value={filters.man_sexual_orientation}
              onValueChange={(value) => setFilters(prev => ({ ...prev, man_sexual_orientation: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {SEXUAL_ORIENTATIONS.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-gray-400 text-xs">Âge</Label>
              <span className="text-xs text-gold">{filters.man_age_min} - {filters.man_age_max} ans</span>
            </div>
            <div className="px-2">
              <Slider
                value={[filters.man_age_min, filters.man_age_max]}
                onValueChange={(values) => {
                  if (values && values.length === 2) {
                    setFilters(prev => ({ ...prev, man_age_min: values[0], man_age_max: values[1] }));
                  }
                }}
                min={18}
                max={99}
                step={1}
                minStepsBetweenThumbs={1}
                className="w-full"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-gray-400 text-xs">Taille</Label>
              <span className="text-xs text-gold">{filters.man_height_min} - {filters.man_height_max} cm</span>
            </div>
            <div className="px-2">
              <Slider
                value={[filters.man_height_min, filters.man_height_max]}
                onValueChange={(values) => {
                  if (values && values.length === 2) {
                    setFilters(prev => ({ ...prev, man_height_min: values[0], man_height_max: values[1] }));
                  }
                }}
                min={140}
                max={220}
                step={1}
                minStepsBetweenThumbs={1}
                className="w-full"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Couleur des yeux</Label>
            <Select
              value={filters.man_eye_color}
              onValueChange={(value) => setFilters(prev => ({ ...prev, man_eye_color: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {EYE_COLORS.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Physique</Label>
            <Select
              value={filters.man_body_type}
              onValueChange={(value) => setFilters(prev => ({ ...prev, man_body_type: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {BODY_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Origine</Label>
            <Select
              value={filters.man_ethnicity}
              onValueChange={(value) => setFilters(prev => ({ ...prev, man_ethnicity: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {ETHNICITIES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Fumeur</Label>
            <Select
              value={filters.man_smoker}
              onValueChange={(value) => setFilters(prev => ({ ...prev, man_smoker: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {SMOKER_STATUS.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Niveau d'expérience</Label>
            <Select
              value={filters.man_experience_level}
              onValueChange={(value) => setFilters(prev => ({ ...prev, man_experience_level: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {EXPERIENCE_LEVELS.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </FilterSectionComponent>
      )}

      {/* Couple Filters - Woman */}
      {isCouple && (
        <FilterSectionComponent title="Critères Femme" section="coupleWoman" expanded={expandedSections["coupleWoman"]} onToggle={toggleSectionCb}>
          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Orientation sexuelle</Label>
            <Select
              value={filters.woman_sexual_orientation}
              onValueChange={(value) => setFilters(prev => ({ ...prev, woman_sexual_orientation: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {SEXUAL_ORIENTATIONS.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-gray-400 text-xs">Âge</Label>
              <span className="text-xs text-gold">{filters.woman_age_min} - {filters.woman_age_max} ans</span>
            </div>
            <div className="px-2">
              <Slider
                value={[filters.woman_age_min, filters.woman_age_max]}
                onValueChange={(values) => {
                  if (values && values.length === 2) {
                    setFilters(prev => ({ ...prev, woman_age_min: values[0], woman_age_max: values[1] }));
                  }
                }}
                min={18}
                max={99}
                step={1}
                minStepsBetweenThumbs={1}
                className="w-full"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <Label className="text-gray-400 text-xs">Taille</Label>
              <span className="text-xs text-gold">{filters.woman_height_min} - {filters.woman_height_max} cm</span>
            </div>
            <div className="px-2">
              <Slider
                value={[filters.woman_height_min, filters.woman_height_max]}
                onValueChange={(values) => {
                  if (values && values.length === 2) {
                    setFilters(prev => ({ ...prev, woman_height_min: values[0], woman_height_max: values[1] }));
                  }
                }}
                min={140}
                max={220}
                step={1}
                minStepsBetweenThumbs={1}
                className="w-full"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Couleur des yeux</Label>
            <Select
              value={filters.woman_eye_color}
              onValueChange={(value) => setFilters(prev => ({ ...prev, woman_eye_color: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {EYE_COLORS.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Physique</Label>
            <Select
              value={filters.woman_body_type}
              onValueChange={(value) => setFilters(prev => ({ ...prev, woman_body_type: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {BODY_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Origine</Label>
            <Select
              value={filters.woman_ethnicity}
              onValueChange={(value) => setFilters(prev => ({ ...prev, woman_ethnicity: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm">
                <SelectValue placeholder="Toutes" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {ETHNICITIES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Fumeur</Label>
            <Select
              value={filters.woman_smoker}
              onValueChange={(value) => setFilters(prev => ({ ...prev, woman_smoker: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {SMOKER_STATUS.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-400 text-xs">Niveau d'expérience</Label>
            <Select
              value={filters.woman_experience_level}
              onValueChange={(value) => setFilters(prev => ({ ...prev, woman_experience_level: value }))}
            >
              <SelectTrigger className="bg-transparent border-white/20 text-white text-sm">
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent className="bg-dark-paper border-white/10">
                {EXPERIENCE_LEVELS.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </FilterSectionComponent>
      )}

      {/* Envies section */}
      <FilterSectionComponent title="Envies" section="envies" expanded={expandedSections["envies"]} onToggle={toggleSectionCb}>
        <div className="flex flex-wrap gap-2">
          {ENVIES.map(envie => {
            const isSelected = filters.envies?.includes(envie.value);
            return (
              <button
                key={envie.value}
                onClick={() => {
                  const newEnvies = isSelected
                    ? filters.envies.filter(e => e !== envie.value)
                    : [...(filters.envies || []), envie.value];
                  setFilters(prev => ({ ...prev, envies: newEnvies }));
                }}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${
                  isSelected
                    ? 'bg-gold text-dark font-semibold'
                    : 'bg-dark-subtle text-gray-400 hover:text-white hover:bg-white/10'
                }`}
              >
                {envie.label}
              </button>
            );
          })}
        </div>
        {filters.envies?.length > 0 && (
          <p className="text-xs text-gray-500 mt-2">
            {filters.envies.length} envie{filters.envies.length > 1 ? 's' : ''} sélectionnée{filters.envies.length > 1 ? 's' : ''}
          </p>
        )}
      </FilterSectionComponent>

      {/* Action buttons */}
      <div className="space-y-3 pt-4 mt-4 border-t border-white/10">
        <Button 
          onClick={() => {
            searchProfiles();
            setShowMobileFilters(false);
          }}
          className="w-full btn-gold"
          data-testid="apply-filters-btn"
        >
          <SearchIcon className="w-4 h-4 mr-2" />
          Rechercher
        </Button>
        
        {hasActiveFilters() && (
          <Button 
            onClick={clearFilters}
            variant="outline"
            className="w-full btn-outline-gold"
            data-testid="clear-filters-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Réinitialiser
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-dark pt-20 md:pt-24 pb-20 md:pb-12" data-testid="search-page">
      {/* Subscription Required Popup */}
      <SubscriptionRequired 
        open={showSubscriptionPopup} 
        onClose={() => setShowSubscriptionPopup(false)} 
        feature="search" 
      />
      
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Access Denied Banner for non-subscribers */}
        {!canSearch && (
          <div className="glass-card p-4 md:p-6 mb-6 md:mb-8 text-center border-gold/30">
            <Lock className="w-8 h-8 md:w-10 md:h-10 text-gold mx-auto mb-3" />
            <h2 className="font-serif text-lg md:text-xl text-white mb-2">Abonnez-vous</h2>
            <p className="text-gray-400 text-sm mb-2">
              Accédez à toutes les fonctionnalités de DiscreetLux
            </p>
            <p className="text-gray-500 text-xs md:text-sm mb-4">
              Photos, messagerie, chat en direct, recherche avancée...
            </p>
            <Link to="/subscription" className="btn-gold inline-flex items-center gap-2">
              <Crown className="w-4 h-4" />
              Voir les abonnements
            </Link>
          </div>
        )}
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6 md:mb-8">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl gold-gradient-text">Rechercher</h1>
            <p className="text-gray-500 text-sm md:text-base mt-1">
              {canSearch ? `${profiles.length} profil${profiles.length !== 1 ? 's' : ''} trouvé${profiles.length !== 1 ? 's' : ''}` : 'Abonnement requis'}
            </p>
          </div>
          
          {/* Mobile filter button */}
          <Button 
            onClick={() => setShowMobileFilters(true)}
            className="lg:hidden btn-outline-gold flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Filtres</span>
            {hasActiveFilters() && (
              <span className="w-2 h-2 bg-gold rounded-full" />
            )}
          </Button>
        </div>

        <div className="flex gap-6 lg:gap-8">
          {/* Main content - Results */}
          <div className="flex-1">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
              </div>
            ) : profiles.length === 0 ? (
              <div className="glass-card p-8 md:p-12 text-center">
                <SearchIcon className="w-10 h-10 md:w-12 md:h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-400">Aucun profil trouvé</p>
                <p className="text-gray-500 text-sm mt-2">Essayez d'élargir vos critères de recherche</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-6" data-testid="profiles-grid">
                  {profiles.map((profile) => (
                    <ProfileCard key={profile.user_id} profile={profile} isSubscribed={user?.is_subscribed} />
                  ))}
                </div>
                
                {/* Message pour utiliser les filtres */}
                {showFilterHint && (
                  <div className="mt-8 p-6 glass-card text-center">
                    <Filter className="w-8 h-8 text-gold mx-auto mb-3" />
                    <p className="text-white font-medium mb-2">
                      {totalProfiles > profiles.length 
                        ? `${profiles.length} profils affichés sur ${totalProfiles} disponibles`
                        : `${profiles.length} profils affichés`
                      }
                    </p>
                    <p className="text-gray-400 text-sm">
                      Utilisez les filtres pour affiner votre recherche et trouver plus de profils correspondant à vos critères
                    </p>
                    <Button 
                      variant="outline" 
                      className="mt-4 border-gold text-gold hover:bg-gold/10"
                      onClick={() => setShowMobileFilters(true)}
                    >
                      <Filter className="w-4 h-4 mr-2" />
                      Ouvrir les filtres
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Desktop Sidebar - Filters on the right */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="glass-card sticky top-28 max-h-[calc(100vh-8rem)] flex flex-col" data-testid="filters-sidebar">
              <div className="flex items-center justify-between p-5 pb-4 border-b border-white/10">
                <h2 className="font-serif text-lg text-white">Filtres</h2>
                {hasActiveFilters() && (
                  <span className="text-xs text-gold bg-gold/10 px-2 py-1 rounded">
                    Actifs
                  </span>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-5 pt-4 scrollbar-visible">
                {filtersContent}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Filters Modal */}
        {showMobileFilters && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="absolute inset-0 bg-black/80" onClick={() => setShowMobileFilters(false)} />
            <div className="absolute right-0 top-0 h-full w-full max-w-sm bg-dark-paper border-l border-white/10 flex flex-col">
              <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
                <h2 className="font-serif text-lg text-white">Filtres</h2>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowMobileFilters(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 pb-24" data-testid="mobile-filters-content">
                {filtersContent}
              </div>
              {/* Fixed search button at bottom for mobile */}
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-dark-paper border-t border-white/10 safe-area-bottom">
                <Button 
                  onClick={() => {
                    searchProfiles();
                    setShowMobileFilters(false);
                  }}
                  className="w-full btn-gold"
                  data-testid="mobile-search-btn"
                >
                  <SearchIcon className="w-4 h-4 mr-2" />
                  Rechercher
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const ProfileCard = ({ profile, isSubscribed }) => {
  return (
    <Link to={`/member/${profile.user_id}`}>
      <div className="glass-card overflow-hidden group hover:border-gold/50 transition-colors" data-testid="profile-card">
        {/* Image */}
        <div className="relative aspect-[4/3] bg-dark-subtle">
          {profile.profile_picture ? (
            <img
              src={getImageUrl(profile.profile_picture)}
              alt={profile.pseudo}
              className={`w-full h-full object-cover ${!isSubscribed && profile.is_blurred ? 'blur-md' : ''}`}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-4xl text-gray-500">{profile.pseudo?.[0]?.toUpperCase()}</span>
            </div>
          )}
          
          {/* Online indicator on image */}
          {profile.is_online && (
            <div className="absolute top-3 left-3">
              <span className="flex items-center gap-1.5 px-2 py-1 bg-dark/80 rounded-full">
                <span className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse" />
                <span className="text-xs text-green-400 font-medium">En ligne</span>
              </span>
            </div>
          )}
          
          {/* Badges - Certifié badge will be shown only when Veriff is integrated */}
          {/* Crown badge removed as per user request */}
          <div className="absolute top-3 right-3 flex gap-2">
            {/* Reserved for Veriff certified badge */}
          </div>

          {/* Blur overlay for non-subscribers */}
          {!isSubscribed && profile.is_blurred && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="text-center">
                <Lock className="w-8 h-8 text-gold mx-auto mb-2" />
                <span className="text-sm text-gold">Abonnement requis</span>
              </div>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-serif text-lg text-white group-hover:text-gold transition-colors inline-flex items-center gap-2">
              {profile.pseudo}
              <OnlineIndicator isOnline={profile.is_online} size="sm" />
            </h3>
            <span className="text-sm text-gray-400">{profile.age} ans</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
            <MapPin className="w-4 h-4" />
            <span>{profile.location || 'Non renseigné'}</span>
            {profile.distance_km !== undefined && profile.distance_km !== null && (
              <>
                <span className="mx-1">•</span>
                <span className="text-gold font-medium">{profile.distance_km} km</span>
              </>
            )}
            <span className="mx-1">•</span>
            <span className="capitalize">{profile.profile_type}</span>
          </div>

          <p className="text-gray-400 text-sm line-clamp-2 whitespace-pre-wrap">{profile.description}</p>

          {profile.photos?.length > 0 && isSubscribed && (
            <div className="flex items-center gap-1 mt-3 text-xs text-gray-500">
              <Eye className="w-3 h-3" />
              <span>{profile.photos.length} photos</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};
